import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useCreateEmployeeBankAccount,
  useEmployeeBankAccounts,
  useUpdateEmployeeBankAccount,
} from '../bankAccounts';
import { BankAccountForm } from '../components/BankAccountForm';
import { useEmployee } from '../queries';
import { employeeName } from '../types';

/** HRM → Employee → Bank accounts → add / edit (routed page, not a modal). */
export function EmployeeBankAccountFormPage() {
  const { publicId = '', accountPublicId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(accountPublicId);

  const { data: employee, isPending: employeePending } = useEmployee(publicId);
  const employeeId = employee?.id ?? -1;
  const { data, isPending: accountsPending } = useEmployeeBankAccounts(employeeId);
  const create = useCreateEmployeeBankAccount(employeeId);
  const update = useUpdateEmployeeBankAccount(employeeId);

  const account = data?.results.find((a) => a.public_id === accountPublicId);
  const back = () => navigate(`/hrm/employees/${publicId}`);

  if (employeePending || (isEdit && accountsPending)) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isEdit && !account) {
    return (
      <div className="max-w-3xl space-y-4">
        <Button variant="ghost" size="sm" className="-ml-2" onClick={back}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-muted-foreground">That bank account no longer exists.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2" onClick={back}>
        <ArrowLeft /> {employee ? employeeName(employee) : 'Employee'}
      </Button>
      <h1 className="text-2xl font-semibold tracking-tight">
        {isEdit ? 'Edit bank account' : 'Add bank account'}
      </h1>

      <BankAccountForm
        account={account}
        submitLabel={isEdit ? 'Save changes' : 'Add account'}
        onCancel={back}
        onSubmit={async (input) => {
          if (account) await update.mutateAsync({ id: account.public_id, input });
          else await create.mutateAsync(input);
          back();
        }}
      />
    </div>
  );
}
